import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '@ajat/design-system/Header';
import SchoolSelector from './SchoolSelector';
import EditOrderLogin from '@ajat/design-system/EditOrderLogin';
import BookMeeting from '@ajat/design-system/BookMeeting';
import { submitMeetingRequest, buildMeetingCalendarUrl } from '../../../../services/meetingApi';
import { useCart } from '../../../../context/CartContext';
import useHeaderMenuIntent from '../../../../hooks/useHeaderMenuIntent';
import useMenuItemVisibility from '../../../../hooks/useMenuItemVisibility';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useBrandSeo from '../../../../hooks/useBrandSeo';
import { readFreshSelection } from '../../../../services/schoolSelectionTtl';
import { shopEntry, schoolShopPath } from '../../../../services/menuDestination';
import { loginToOrder } from '../../Common/EditOrder/ordersApi';

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-DK — Common Header (TopBar + Header)
 * Single source of truth for header configuration across all CLS-DK pages
 * ══════════════════════════════════════════════════════════════════════════ */

// navItems/mobileSettings are built per instance so the two fixed menu items
// (Studieturstrøjer = menu_1, Vogntrøjer = menu_2) can resolve to the
// customer's mapped school shop via onMenuItemClick (useHeaderMenuIntent).
const buildMobileSettings = (onMenuItemClick, showMenu1Promo = true) => ({
  mobileTitle: 'Menu',
  // The promo card is tied to menu_1 — hide it when the item itself is hidden
  // (open shop disabled + the customer's school has no menu_1 shop).
  ...(showMenu1Promo ? {
    mobilePromo: {
      cardType: 'button',
      image: require('../../../../assets/cls-dk/header/1.jpg'),
      imageAlt: 'Student trip hoodie',
      label: 'BESTIL STUDIETURTRØJER',
      href: '#',
      onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_1'); },
    },
  } : {}),
});

// `menuKey` tags the two fixed menu-item entries so the header can hide one
// when the "Disable Open Shop" flag is on and the customer's school has no
// open shop of that type (useMenuItemVisibility). Untagged entries never hide.
const buildNavItems = (onMenuItemClick) => [
  {
    label: 'Studieturstrøjer',
    menuKey: 'menu_1',
    href: '#',
    onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_1'); },
    submenu: {
      watermark: 'Studieturstrøjer',
      columns: [
        { type: 'card', cardType: 'button', image: require('../../../../assets/cls-dk/header/1.jpg'), imageAlt: 'Student trip hoodie', label: 'BESTIL STUDIETURTRØJER', href: '#', onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_1'); } },
        { type: 'card', cardType: 'link', image: require('../../../../assets/cls-dk/header/2.jpg'), imageAlt: 'AI logo generator', label: 'AI logo generator', href: '/clothing/cls-dk/logo-generator' },
        { type: 'card', cardType: 'link', image: require('../../../../assets/cls-dk/header/3.jpg'), imageAlt: 'School shop', label: 'Upload din tegning', href: '/clothing/cls-dk/create-school' },
        {
          type: 'links',
          items: [
            { label: 'Inspiration til tegninger', href: '/clothing/cls-dk/ideas' },
            { label: 'FAQ', href: '/clothing/cls-dk/faq' },
            { label: 'Størrelses guide', href: '/clothing/cls-dk/size-guide' },
            { label: 'Tryk & Broderi', href: '/clothing/cls-dk/tryk-broderi' },
            
          ],
        },
      ],
    },
  },
  {
    label: 'Vogntrøjer',
    menuKey: 'menu_2',
    href: '#',
    onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_2'); },
    submenu: {
      watermark: 'Vogntrøjer',
      columns: [
        { type: 'card', cardType: 'button', image: require('../../../../assets/cls-dk/header/4.jpg'), imageAlt: 'Vogntrøjer', label: 'BESTIL VOGNTRØJER', href: '#', onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_2'); } },
        {
          type: 'links',
          items: [
            { label: 'FAQ', href: '/clothing/cls-dk/faq' },
            { label: 'Størrelses guide', href: '/clothing/cls-dk/size-guide' },
            { label: 'Tryk & Broderi', href: '/clothing/cls-dk/tryk-broderi' },
          ],
        },
      ],
    },
  },
  {
    label: 'FAQ',
    href: '/clothing/cls-dk/faq',
  },
  {
    label: 'Studenterhue',
    href: 'https://clseifert.com/dk',
  },
];

const topBarNotice = '+ 75.000 KUNDER ÅRLIGT';

const topBarLinks = [
  { label: 'FAQ', href: '/clothing/cls-dk/faq' },
];

/* The product pages mount this Header twice (desktop + mobile wrappers, one of
 * them display:none) and may fire the open-school-selector event while only the
 * hidden one exists (loading branch). A hidden instance parks the request here;
 * the next VISIBLE instance to mount consumes it, so the popup always appears
 * exactly once, in the header the customer can see. */
let parkedSelectorOpen = null;
let lastSelectorOpenAt = 0;

const CLSDKHeader = ({
  showTopBar = true,
  hideOnScroll = true,
  onSchoolClick,
  schoolSelectorLockScroll = true,
  ...headerProps
}) => {
  const navigate = useNavigate();
  // Sibling routes that share one route element (e.g. /shop/:menuSlug) keep
  // this Header mounted, so the DS component needs a navigation signal to
  // close its open menus. location.key changes on EVERY history entry —
  // including a menu click that re-navigates to the page you are already on.
  const location = useLocation();
  const cart = useCart('clsdk');
  const { showSchoolSelector } = useBrandSettings('clsdk');
  useBrandSeo('clsdk');
  const [isSchoolSelectorOpen, setIsSchoolSelectorOpen] = useState(false);
  const [isEditOrderLoginOpen, setIsEditOrderLoginOpen] = useState(false);
  const [isBookMeetingOpen, setIsBookMeetingOpen] = useState(false);
  const [isBookingMeeting, setIsBookingMeeting] = useState(false);
  const [pendingNavigate, setPendingNavigate] = useState(null);
  // Mandatory identification mode (product pages): popup is non-dismissible
  // and, when the picked school matches `staySchool` (or it is '*'), the
  // customer STAYS on the page they came from after selecting.
  const [mandatorySelection, setMandatorySelection] = useState(false);
  // School + shop known from the URL that opened the popup (see
  // useForceSchoolSelection `prefill`); cleared when the popup closes.
  const [selectorPrefill, setSelectorPrefill] = useState(null);
  const staySchoolRef = useRef(null);
  // The product pages mount this Header twice (desktop + mobile wrappers, one of
  // them display:none). Only the VISIBLE instance may open the school popup —
  // the hidden one would render an invisible overlay that still locks the page.
  const rootRef = useRef(null);
  const isVisibleInstance = () => !rootRef.current || rootRef.current.getClientRects().length > 0;

  // Menu-item → mapped-shop resolution for the two fixed header items.
  const {
    onMenuItemClick,
    readIntentFromEvent,
    clearIntent,
    preferredSeasonResolver,
    showPreferredSeason,
    navigateForIntentAfterSelect,
  } = useHeaderMenuIntent({ brandSlug: 'clsdk', feSlug: 'cls-dk' });

  // When the open shop is disabled and the customer's school has no open shop
  // of that type, the menu-tied entry is neutralized — but its section stays,
  // because the submenu carries other links (logo generator, create-school,
  // FAQ, ...): the root label stops navigating and the buy card (the button
  // card) is dropped from the submenu. Untagged entries always stay whole.
  const { isVisible, isHidden, isUnmappedForSchool } = useMenuItemVisibility('clsdk');
  const navItems = useMemo(
    () => buildNavItems(onMenuItemClick).flatMap((item) => {
      // Admin-hidden, or unmapped for the customer's school (no shop / group
      // linked to it) → gone, submenu included.
      if (item.menuKey && (isHidden(item.menuKey) || isUnmappedForSchool(item.menuKey))) return [];
      if (!item.menuKey || isVisible(item.menuKey)) return [item];
      if (!item.submenu) return [];
      return [{
        ...item,
        onClick: (e) => e.preventDefault(),
        submenu: {
          ...item.submenu,
          columns: item.submenu.columns.filter((c) => !(c.type === 'card' && c.cardType === 'button')),
        },
      }];
    }),
    [onMenuItemClick, isVisible, isHidden, isUnmappedForSchool],
  );
  const mobileSettings = useMemo(
    () => buildMobileSettings(onMenuItemClick, isVisible('menu_1')),
    [onMenuItemClick, isVisible],
  );

  // Show the selected school's name on the header school button (read fresh
  // each render — selecting/clearing re-renders via the selector state).
  const storedSchoolName = readFreshSelection('clsdk_school_selection')?.school?.name || null;

  /* Build minicart items from cart context */
  const priceSuffix = ',-';
  const cartItems = [...cart.products, ...cart.packages].map((item) => {
    const opts = item.options || {};
    const details = [];
    if (opts.color) details.push(opts.color + (opts.size ? ` ${opts.size}` : ''));
    else if (opts.size) details.push(opts.size);
    if (opts.addons) {
      if (opts.addons.navnetryk) details.push(`Navnetryk: ${opts.addons.navnetryk}`);
      if (opts.addons.lilleBrysttryk) details.push('Lille Brysttryk');
    }
    // Bonus products (server-appended, free) stay out of the minicart count.
    const visibleIncluded = (item.includedProducts || []).filter((ip) => !ip?.bonus);
    if (visibleIncluded.length) details.push(`${visibleIncluded.length} produkter inkluderet`);
    return {
      id: item.id,
      name: item.name,
      image: opts.image || item.image,
      details,
      quantity: item.quantity,
      price: `${Math.round(item.lineTotal)}${priceSuffix}`,
    };
  });

  useEffect(() => {
    // Selector disabled for the brand → never open the popup from the event.
    if (!showSchoolSelector) return undefined;
    const applyOpen = (e) => {
      setIsSchoolSelectorOpen(true);
      setPendingNavigate(e.detail?.navigateTo ?? null);
      setMandatorySelection(Boolean(e.detail?.mandatory));
      setSelectorPrefill(e.detail?.prefill ?? null);
      staySchoolRef.current = e.detail?.mandatory ? (e.detail?.staySchool ?? null) : null;
      readIntentFromEvent(e);
      lastSelectorOpenAt = Date.now();
      parkedSelectorOpen = null;
    };
    const handleOpen = (e) => {
      if (!isVisibleInstance()) { parkedSelectorOpen = { detail: e.detail, at: Date.now() }; return; }
      applyOpen(e);
    };
    // A request parked by a hidden instance before this (visible) one mounted.
    if (parkedSelectorOpen && parkedSelectorOpen.at > lastSelectorOpenAt
      && Date.now() - parkedSelectorOpen.at < 15000 && isVisibleInstance()) {
      applyOpen(new CustomEvent('open-school-selector', { detail: parkedSelectorOpen.detail }));
    }
    document.addEventListener('open-school-selector', handleOpen);
    return () => {
      document.removeEventListener('open-school-selector', handleOpen);
    };
  }, [showSchoolSelector, readIntentFromEvent]);

  const handleSchoolClick = () => {
    if (!showSchoolSelector) return;
    setIsSchoolSelectorOpen(true);
    setPendingNavigate(null);
    setMandatorySelection(false);
    staySchoolRef.current = null;
    clearIntent();
    if (onSchoolClick) onSchoolClick();
  };

  const handleUserClick = () => {
    setIsEditOrderLoginOpen(true);
  };

  // Returning from StudentMeet via the browser back button restores this page
  // from the bfcache with React state intact — clear the submitting flag so
  // the CTA isn't stuck in its loading state forever.
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) setIsBookingMeeting(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // "Min skole er ikke på listen" → capture the lead, then hand the customer
  // over to the StudentMeet calendar. The POST is best-effort: the booking is
  // what the customer came for, so a failed (or not-yet-deployed) lead endpoint
  // must never block the redirect.
  const handleBookMeetingSubmit = async (values) => {
    setIsBookingMeeting(true);
    try {
      await submitMeetingRequest('clsdk', values);
    } catch {
      /* lead capture is best-effort — continue to the calendar */
    }
    window.location.href = buildMeetingCalendarUrl({ returnPath: '/clothing/cls-dk' });
  };

  const handleNavigateAfterSelect = (selection) => {
    if (pendingNavigate) {
      const target = pendingNavigate;
      setPendingNavigate(null);
      navigate(target);
    } else if (location.pathname === '/clothing/cls-dk') {
      // Plain login from the landing's header button: stay on the landing —
      // the header shows the school, and every card / CTA click now resolves
      // into the customer's own shop → sub-shop → item.
    } else if (selection?.school?.slug) {
      // Elsewhere: land in the chosen shop's entry page — the first group of
      // the shop the customer picked (never a grouped shop's root), else the
      // shop root, else the whole-school page.
      const { shop, group } = shopEntry(selection.school, null, selection.season ?? null);
      navigate(shop ? schoolShopPath('cls-dk', selection.school.slug, shop, group) : `/clothing/cls-dk/school-shop/${selection.school.slug}`);
    } else {
      navigate('/clothing/cls-dk/school-shop');
    }
  };

  return (
    <>
      <span ref={rootRef} aria-hidden="true" style={{ display: 'block', width: 0, height: 0 }} />
      <Header
        brand="CLS"
        subBrand="main"
        mode="default"
        logoHref="/clothing/cls-dk"
        onLogoClick={(e) => { e.preventDefault(); navigate('/clothing/cls-dk'); }}
        schoolLabel={storedSchoolName || undefined}
        showTopBar={showTopBar}
        topBarNotice={topBarNotice}
        topBarLinks={topBarLinks}
        hideOnScroll={hideOnScroll}
        navItems={navItems}
        mobileSettings={mobileSettings}
        routeKey={location.key}
        onSchoolClick={handleSchoolClick}
        showSchoolButton={showSchoolSelector}
        cartCount={cart.itemCount}
        cartItems={cartItems}
        cartTotal={`${Math.round(cart.total)},-`}
        cartItemsLabel="Varer"
        cartTotalLabel="Total"
        cartCtaLabel="Til Kassen"
        onCartCtaClick={() => navigate('/clothing/cls-dk/shopping-cart')}
        showUserButton
        orderLabel="Rediger ordre"
        onUserClick={handleUserClick}
        {...headerProps}
      />
      <SchoolSelector
        isOpen={showSchoolSelector && isSchoolSelectorOpen}
        dismissible={!mandatorySelection}
        onClose={() => {
          setIsSchoolSelectorOpen(false);
          setPendingNavigate(null);
          setMandatorySelection(false);
          setSelectorPrefill(null);
          // Dismissing just closes the popup; drop the pending menu intent.
          clearIntent();
        }}
        onSelect={(selection) => {
          setIsSchoolSelectorOpen(false);
          setMandatorySelection(false);
          setSelectorPrefill(null);
          // Mandatory identification from a product page: stay on that page
          // when the picked school matches (or staySchool is '*') — the
          // customer proceeds with the product they came for.
          const stay = staySchoolRef.current;
          staySchoolRef.current = null;
          if (stay && (stay === '*' || selection?.school?.slug === stay)) return;
          // A pending menu intent navigates to the mapped shop (or fallback).
          if (navigateForIntentAfterSelect(selection)) return;
          handleNavigateAfterSelect(selection);
        }}
        prefill={selectorPrefill}
        preferredSeasonResolver={preferredSeasonResolver}
        showPreferredSeason={showPreferredSeason}
        onNotFoundClick={() => {
          setPendingNavigate(null);
          navigate('/clothing/cls-dk/create-school');
        }}
        onNotInListCtaClick={() => {
          setIsSchoolSelectorOpen(false);
          setPendingNavigate(null);
          clearIntent();
          setIsBookingMeeting(false);
          setIsBookMeetingOpen(true);
        }}
        lockScroll={schoolSelectorLockScroll}
        notFoundTitle="Kan du ikke finde din skole?"
      />
      <BookMeeting
        isOpen={isBookMeetingOpen}
        onClose={() => {
          setIsBookMeetingOpen(false);
          setIsBookingMeeting(false);
        }}
        onSubmit={handleBookMeetingSubmit}
        isSubmitting={isBookingMeeting}
        title="Mangler du og din klasse hjælp til jeres studieturstøj?"
        infoText="At stå for klassens studieturstøj kan være en stor opgave. Men bare rolig, vi er her for at hjælp jer. Hvert år hjælper vi tusindvis af studerende og vi sidder naturligvis også klar til at hjælp jer. Udfyld formularen herunder, vælg den tid og dato der passer jer, så sidder vi klar til at hjælp jer."
        nameLabel="Navn"
        emailLabel="Email"
        phoneLabel="Telefon nr."
        submitLabel="Gå til mødekalender"
        nameErrorMessage="Indtast dit navn"
        emailErrorMessage="Indtast en gyldig e-mailadresse"
        phoneErrorMessage="Indtast et gyldigt telefonnummer"
      />
      <EditOrderLogin
        onLogin={loginToOrder}
        isOpen={isEditOrderLoginOpen}
        onClose={() => setIsEditOrderLoginOpen(false)}
        apiBrandSlug="clsdk"
        onSuccess={({ orderNumber, token, order }) => {
          setIsEditOrderLoginOpen(false);
          navigate(`/clothing/cls-dk/edit-order/${orderNumber}`, { state: { token, order } });
        }}
        notFoundMessage="Vi kunne ikke finde en ordre med den e-mail og det ordrenummer."
        title="Rediger ordre"
        infoText="Du kan finde dit ordrenummer i bekræftelsesmailen, vi har sendt til dig."
        emailLabel="E-mail"
        orderNumberLabel="Ordrenummer"
        submitLabel="Fortsæt"
        emailErrorMessage="Indtast en gyldig e-mailadresse"
        orderNumberErrorMessage="Indtast et gyldigt ordrenummer"
      />
    </>
  );
};

export default CLSDKHeader;
